import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const btnStyle = (disabled) => ({
  padding: '4px 12px',
  fontSize: '10px',
  backgroundColor: disabled ? '#2a2d3a' : '#3fb950',
  border: 'none',
  borderRadius: '3px',
  color: disabled ? '#6e7681' : '#ffffff',
  cursor: disabled ? 'not-allowed' : 'pointer'
});

// Categorize a script by name: 'takermaker', 'funding', or 'unknown'
const categorizeScript = (scriptName) => {
  const name = (scriptName || '').toLowerCase();
  if (name.includes('takermaker')) return 'takermaker';
  if (name.includes('funding')) return 'funding';
  return 'unknown';
};

const CHART_COLORS = {
  symbols: '#58a6ff',
  takermaker: '#3fb950',
  futures: '#d2a8ff',
  spot: '#ffa657',
  duration: '#f78166',
};

// Tooltip formatter for the charts — shows only values that are not null
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{ background: '#1c2128', border: '1px solid #30363d', borderRadius: '4px', padding: '8px 12px', fontSize: '12px' }}>
      <p style={{ color: '#8b949e', margin: '0 0 4px 0' }}>{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color, margin: '2px 0' }}>
          {p.name}: {p.value ?? '---'}
        </p>
      ))}
    </div>
  );
};

const Admin = () => {
  const { user } = useAuth();

  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedScript, setSelectedScript] = useState(null);

  // All records for charting
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    document.title = 'Admin';
  }, []);

  useEffect(() => {
    if (user) fetchSummary();
  }, [user]); // eslint-disable-line

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/downloader-log/summary`, {
        headers: { 'x-user-id': user.sub }
      });
      const data = await res.json();
      if (res.status === 401 || res.status === 403) {
        setError(data.error || 'Access denied.');
        return;
      }
      if (!data.success) throw new Error(data.error || 'Failed to fetch summary');
      setSummary(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all records for charting (sorted oldest-first for the chart X axis)
  const fetchChartData = useCallback(async (scriptName) => {
    setChartLoading(true);
    setChartData([]);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/downloader-log/script/${encodeURIComponent(scriptName)}?page=1&pageSize=all`,
        { headers: { 'x-user-id': user.sub } }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      // Sort oldest → newest so the chart reads left-to-right chronologically
      const sorted = [...data.data].sort(
        (a, b) => new Date(a.stop_time_date) - new Date(b.stop_time_date)
      );
      setChartData(sorted.map(row => ({
        label: row.stop_time_date
          ? new Date(row.stop_time_date).toLocaleString('en-GB', {
              month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit', hour12: false
            })
          : '---',
        symbols: row.symbols_processed ?? null,
        takermaker: row.symbols_takermaker_processed ?? null,
        futures: row.symbols_futures_processed ?? null,
        spot: row.symbols_spot_processed ?? null,
        duration: row.duration ?? null,
      })));
    } catch (err) {
      console.error('Failed to fetch chart data:', err);
    } finally {
      setChartLoading(false);
    }
  }, [user]);

  const handleScriptClick = (scriptName) => {
    setSelectedScript(scriptName);
    fetchChartData(scriptName);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '---';
    return new Date(dateStr).toLocaleString('en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  };

  const formatDuration = (seconds) => {
    if (seconds == null) return '---';
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  const TakerMakerHead = () => (
    <thead>
      <tr>
        <th data-label="Script Name">Script Name</th>
        <th data-label="Exchange">Exchange</th>
        <th data-label="Start Time">Start Time</th>
        <th data-label="Stop Time">Stop Time</th>
        <th data-label="Duration">Duration</th>
        <th data-label="Symbols">Symbols</th>
        <th data-label="Taker/Maker">Taker/Maker</th>
      </tr>
    </thead>
  );

  const FundingHead = () => (
    <thead>
      <tr>
        <th data-label="Script Name">Script Name</th>
        <th data-label="Exchange">Exchange</th>
        <th data-label="Start Time">Start Time</th>
        <th data-label="Stop Time">Stop Time</th>
        <th data-label="Duration">Duration</th>
        <th data-label="Symbols">Symbols</th>
        <th data-label="Futures">Futures</th>
      </tr>
    </thead>
  );

  const TakerMakerRow = ({ row, clickable, isActive }) => (
    <tr
      onClick={clickable ? () => handleScriptClick(row.script_name) : undefined}
      style={{ cursor: clickable ? 'pointer' : 'default', backgroundColor: isActive ? 'rgba(255,255,255,0.05)' : 'inherit' }}
      title={clickable ? 'Click to view full history' : undefined}
    >
      <td>{row.script_name}</td>
      <td>{row.exchange_name || '---'}</td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.start_time_date)}</td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.stop_time_date)}</td>
      <td>{formatDuration(row.duration)}</td>
      <td>{row.symbols_processed ?? '---'}</td>
      <td>{row.symbols_takermaker_processed ?? '---'}</td>
    </tr>
  );

  const FundingRow = ({ row, clickable, isActive }) => (
    <tr
      onClick={clickable ? () => handleScriptClick(row.script_name) : undefined}
      style={{ cursor: clickable ? 'pointer' : 'default', backgroundColor: isActive ? 'rgba(255,255,255,0.05)' : 'inherit' }}
      title={clickable ? 'Click to view full history' : undefined}
    >
      <td>{row.script_name}</td>
      <td>{row.exchange_name || '---'}</td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.start_time_date)}</td>
      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.stop_time_date)}</td>
      <td>{formatDuration(row.duration)}</td>
      <td>{row.symbols_processed ?? '---'}</td>
      <td>{row.symbols_futures_processed ?? '---'}</td>
    </tr>
  );

  const SummaryTable = ({ rows, category }) => {
    const isTakerMaker = category === 'takermaker';
    return (
      <div className="data-table" style={{ marginBottom: '8px' }}>
        <table>
          {isTakerMaker ? <TakerMakerHead /> : <FundingHead />}
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#8b949e', padding: '16px' }}>
                  No records in the last 24 hours
                </td>
              </tr>
            ) : rows.map(row =>
              isTakerMaker
                ? <TakerMakerRow key={row.log_item_id} row={row} clickable={true} isActive={selectedScript === row.script_name} />
                : <FundingRow key={row.log_item_id} row={row} clickable={true} isActive={selectedScript === row.script_name} />
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Charts rendered for the selected script
  const ScriptCharts = ({ category }) => {
    if (chartLoading) return <div className="loading" style={{ marginTop: '24px' }}>Loading charts...</div>;
    if (chartData.length === 0) return null;

    const isTakerMaker = category === 'takermaker';

    // Determine tick interval so we don't show thousands of X labels
    // Show at most ~10 ticks; interval = how many points to skip
    const tickInterval = Math.max(0, Math.floor(chartData.length / 10) - 1);

    const chartContainerStyle = {
      marginTop: '28px',
      background: '#161b22',
      border: '1px solid #30363d',
      borderRadius: '6px',
      padding: '16px 8px 8px 8px',
    };

    return (
      <>
        {/* Chart 1: Symbols / Taker/Maker / Futures */}
        <div style={chartContainerStyle}>
          <p style={{ color: '#8b949e', fontSize: '12px', margin: '0 0 12px 16px' }}>
            Symbols processed over time
          </p>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis
                dataKey="label"
                tick={{ fill: '#8b949e', fontSize: 10 }}
                interval={tickInterval}
                angle={-30}
                textAnchor="end"
                height={48}
              />
              <YAxis tick={{ fill: '#8b949e', fontSize: 10 }} width={48} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#8b949e' }} />
              <Line
                type="monotone"
                dataKey="symbols"
                name="Symbols"
                stroke={CHART_COLORS.symbols}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
              {isTakerMaker && (
                <Line
                  type="monotone"
                  dataKey="takermaker"
                  name="Taker/Maker"
                  stroke={CHART_COLORS.takermaker}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              )}
              {!isTakerMaker && (
                <Line
                  type="monotone"
                  dataKey="spot"
                  name="Spot"
                  stroke={CHART_COLORS.spot}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              )}
              <Line
                type="monotone"
                dataKey="futures"
                name="Futures"
                stroke={CHART_COLORS.futures}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Chart 2: Duration */}
        <div style={{ ...chartContainerStyle, marginTop: '16px' }}>
          <p style={{ color: '#8b949e', fontSize: '12px', margin: '0 0 12px 16px' }}>
            Duration (seconds) over time
          </p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis
                dataKey="label"
                tick={{ fill: '#8b949e', fontSize: 10 }}
                interval={tickInterval}
                angle={-30}
                textAnchor="end"
                height={48}
              />
              <YAxis tick={{ fill: '#8b949e', fontSize: 10 }} width={48} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#8b949e' }} />
              <Line
                type="monotone"
                dataKey="duration"
                name="Duration (s)"
                stroke={CHART_COLORS.duration}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </>
    );
  };

  if (loading) return <div className="loading">Loading admin data...</div>;

  const takerMakerRows = summary.filter(r => categorizeScript(r.script_name) === 'takermaker');
  const fundingRows = summary.filter(r => categorizeScript(r.script_name) === 'funding');
  const unknownRows = summary.filter(r => categorizeScript(r.script_name) === 'unknown');

  const selectedCategory = selectedScript ? categorizeScript(selectedScript) : null;

  return (
    <div className="page-content">
      <h2 style={{ marginBottom: '16px' }}>Administration</h2>

      {error && (
        <div className="error" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#3d1c1c', color: '#f85149', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {!error && (
        <>
          <p style={{ color: '#8b949e', marginBottom: '20px', fontSize: '13px' }}>
            Latest completed run per script in the last 24 hours. Click a row to view charts.
          </p>

          {/* Taker/Maker summary table */}
          <h3 style={{ marginBottom: '8px' }}>Taker/Maker Scripts</h3>
          <SummaryTable rows={takerMakerRows} category="takermaker" />

          {/* Funding summary table */}
          <h3 style={{ marginBottom: '8px', marginTop: '28px' }}>Funding Scripts</h3>
          <SummaryTable rows={fundingRows} category="funding" />

          {/* Unknown scripts — plain text */}
          {unknownRows.length > 0 && (
            <div style={{ marginTop: '28px' }}>
              <h3 style={{ marginBottom: '8px' }}>Unrecognized Scripts</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {unknownRows.map(row => (
                  <div
                    key={row.log_item_id}
                    style={{ color: '#8b949e', fontSize: '13px', padding: '6px 10px', background: '#161b22', borderRadius: '4px', cursor: 'pointer', border: selectedScript === row.script_name ? '1px solid #3fb950' : '1px solid #30363d' }}
                    onClick={() => handleScriptClick(row.script_name)}
                    title="Click to view full history"
                  >
                    <strong style={{ color: '#c9d1d9' }}>{row.script_name}</strong>
                    {row.exchange_name && <span> — {row.exchange_name}</span>}
                    <span> | start: {formatDate(row.start_time_date)}</span>
                    <span> | stop: {formatDate(row.stop_time_date)}</span>
                    <span> | duration: {formatDuration(row.duration)}</span>
                    <span> | symbols: {row.symbols_processed ?? '---'}</span>
                    <span> | spot: {row.symbols_spot_processed ?? '---'}</span>
                    <span> | futures: {row.symbols_futures_processed ?? '---'}</span>
                    <span> | taker/maker: {row.symbols_takermaker_processed ?? '---'}</span>
                    <span> | opportunities: {row.opportunities_found ?? '---'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Charts section for selected script */}
          {selectedScript && (
            <div style={{ marginTop: '36px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px', flexWrap: 'wrap', gap: '8px' }}>
                <h3 style={{ margin: 0 }}>Charts: {selectedScript}</h3>
                <button onClick={() => setSelectedScript(null)} style={{ ...btnStyle(false), padding: '4px 16px' }}>
                  Close
                </button>
              </div>
              <ScriptCharts category={selectedCategory} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Admin;
